import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CrudApiOptions, GenResp } from "@template-ui/services/crud-api.service";
import { AppBaseService } from "@app/shared/services/app-base.service";
import {
    PlatformTerminologyVocabularyCatalogQuery,
    PlatformTerminologyVocabularyDraftContextQuery,
} from "@app/core/APIs/app-apis";
import { map, Observable } from "rxjs";
import {
    mapTerminologyAdminError,
    TerminologyEntryCreateRequest,
    TerminologyEntryDetail,
    TerminologyEntryUpdateRequest,
    TerminologyUsageManifest,
    TerminologyVocabularyCatalog,
    TerminologyVocabularyContentDiffDetail,
    TerminologyVocabularyDefinitionDetail,
    TerminologyVocabularyDefinitionCreateRequest,
    TerminologyVocabularyDraftCreateRequest,
    TerminologyVocabularyDraftDetail,
    TerminologyVocabularyDraftPublishRequest,
    TerminologyVocabularyDraftSaveRequest,
    TerminologyVocabularyDraftValidationRequest,
    TerminologyVocabularyPublicationDetail,
    TerminologyVocabularyPublicationHistoryItem,
    TerminologyVocabularyPublicationRetireRequest,
    TerminologyVocabularyRevisionHistoryItem,
    TerminologyVocabularyRevisionRestoreRequest,
    TerminologyVocabularyValidationImpactResult,
    TerminologyVocabularyWordingUpdateRequest,
} from "./platform-terminology-admin.models";

const requestHeaders = new HttpHeaders({
    "Content-Type": "application/json",
    Accept: "application/json, text/plain, */*",
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
});

const terminologyAdminApiOptions: Partial<CrudApiOptions<"body", "json">> = {
    retries: 0,
    feedbackOptions: {
        successMode: "none",
        errorMode: "none",
    },
    getOptions: {
        responseType: "json",
        headers: requestHeaders,
        withCredentials: true,
    },
    postOptions: {
        responseType: "json",
        headers: requestHeaders,
        withCredentials: true,
    },
    putOptions: {
        responseType: "json",
        headers: requestHeaders,
        withCredentials: true,
    },
};

export interface TerminologyVocabularyMutationIntent {
    readonly idempotencyKey: string;
}

export function createTerminologyVocabularyMutationIntent(): TerminologyVocabularyMutationIntent {
    const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    return {
        idempotencyKey: cryptoApi?.randomUUID
            ? cryptoApi.randomUUID()
            : `terminology-vocabulary-${Date.now()}-${Math.round(Math.random() * 1e9)}`,
    };
}

@Injectable({ providedIn: "root" })
export class PlatformTerminologyAdminService extends AppBaseService {
    getODataEndpoint(): string {
        return this.apis.core.manual.getPlatformConfigurationTerminologyEntriesOData();
    }

    getUsageManifest(): Observable<TerminologyUsageManifest> {
        return this.crudService.getbyUrl<TerminologyUsageManifest>(
            this.apis.core.manual.getPlatformConfigurationTerminologyUsageManifest(),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getVocabularyCatalog(
        query: PlatformTerminologyVocabularyCatalogQuery,
    ): Observable<TerminologyVocabularyCatalog> {
        return this.crudService.getbyUrl<TerminologyVocabularyCatalog>(
            this.apis.core.manual.getPlatformConfigurationTerminologyVocabularyCatalog(query),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getVocabularyDefinition(definitionId: number): Observable<TerminologyVocabularyDefinitionDetail> {
        return this.crudService.getbyUrl<TerminologyVocabularyDefinitionDetail>(
            this.apis.core.manual.getPlatformConfigurationTerminologyVocabularyDefinition(definitionId),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    createVocabularyDefinition(
        request: TerminologyVocabularyDefinitionCreateRequest,
        intent = createTerminologyVocabularyMutationIntent(),
    ): Observable<TerminologyVocabularyPublicationDetail> {
        return this.crudService.postByUrl<TerminologyVocabularyPublicationDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyVocabularyDefinitions(),
            request,
            this.mutationOptions("postOptions", intent),
        ).pipe(map((response) => unwrapResponse(response)));
    }

    updateVocabularyWording(
        definitionId: number,
        request: TerminologyVocabularyWordingUpdateRequest,
        intent = createTerminologyVocabularyMutationIntent(),
    ): Observable<TerminologyVocabularyPublicationDetail> {
        return this.crudService.putByUrl<TerminologyVocabularyPublicationDetail>(
            this.apis.core.manual.putPlatformConfigurationTerminologyVocabularyDefinitionWording(definitionId),
            request,
            this.mutationOptions("putOptions", intent),
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getVocabularyDraft(draftId: number): Observable<TerminologyVocabularyDraftDetail> {
        return this.crudService.getbyUrl<TerminologyVocabularyDraftDetail>(
            this.apis.core.manual.getPlatformConfigurationTerminologyVocabularyDraft(draftId),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getVocabularyDefinitionDraft(
        definitionId: number,
        context: PlatformTerminologyVocabularyDraftContextQuery,
    ): Observable<TerminologyVocabularyDraftDetail> {
        return this.crudService.getbyUrl<TerminologyVocabularyDraftDetail>(
            this.apis.core.manual.getPlatformConfigurationTerminologyVocabularyDefinitionCurrentDraft(
                definitionId,
                context,
            ),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    createVocabularyDraft(
        definitionId: number,
        request: TerminologyVocabularyDraftCreateRequest,
        intent = createTerminologyVocabularyMutationIntent(),
    ): Observable<TerminologyVocabularyDraftDetail> {
        return this.crudService.postByUrl<TerminologyVocabularyDraftDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyVocabularyDefinitionDrafts(definitionId),
            request,
            this.mutationOptions("postOptions", intent),
        ).pipe(map((response) => unwrapResponse(response)));
    }

    saveVocabularyDraft(
        draftId: number,
        request: TerminologyVocabularyDraftSaveRequest,
        intent = createTerminologyVocabularyMutationIntent(),
    ): Observable<TerminologyVocabularyDraftDetail> {
        return this.crudService.putByUrl<TerminologyVocabularyDraftDetail>(
            this.apis.core.manual.putPlatformConfigurationTerminologyVocabularyDraft(draftId),
            request,
            this.mutationOptions("putOptions", intent),
        ).pipe(map((response) => unwrapResponse(response)));
    }

    validateVocabularyDraft(
        draftId: number,
        request: TerminologyVocabularyDraftValidationRequest,
    ): Observable<TerminologyVocabularyValidationImpactResult> {
        return this.crudService.postByUrl<TerminologyVocabularyValidationImpactResult>(
            this.apis.core.manual.postPlatformConfigurationTerminologyVocabularyDraftValidate(draftId),
            request,
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getVocabularyDraftDiff(draftId: number): Observable<TerminologyVocabularyContentDiffDetail> {
        return this.crudService.getbyUrl<TerminologyVocabularyContentDiffDetail>(
            this.apis.core.manual.getPlatformConfigurationTerminologyVocabularyDraftDiff(draftId),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getVocabularyRevisionDiff(
        definitionId: number,
        fromRevisionId: number,
        toRevisionId: number,
    ): Observable<TerminologyVocabularyContentDiffDetail> {
        return this.crudService.getbyUrl<TerminologyVocabularyContentDiffDetail>(
            this.apis.core.manual.getPlatformConfigurationTerminologyVocabularyDefinitionRevisionDiff(
                definitionId,
                fromRevisionId,
                toRevisionId,
            ),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getVocabularyHistory(
        definitionId: number,
        configurationVersionId?: number,
    ): Observable<TerminologyVocabularyRevisionHistoryItem[]> {
        return this.crudService.getbyUrl<TerminologyVocabularyRevisionHistoryItem[]>(
            this.apis.core.manual.getPlatformConfigurationTerminologyVocabularyDefinitionHistory(
                definitionId,
                configurationVersionId,
            ),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    publishVocabularyDraft(
        draftId: number,
        request: TerminologyVocabularyDraftPublishRequest,
        intent = createTerminologyVocabularyMutationIntent(),
    ): Observable<TerminologyVocabularyPublicationDetail> {
        return this.crudService.postByUrl<TerminologyVocabularyPublicationDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyVocabularyDraftPublish(draftId),
            request,
            this.mutationOptions("postOptions", intent),
        ).pipe(map((response) => unwrapResponse(response)));
    }

    retireVocabularyPublication(
        publicationId: number,
        request: TerminologyVocabularyPublicationRetireRequest,
        intent = createTerminologyVocabularyMutationIntent(),
    ): Observable<TerminologyVocabularyPublicationHistoryItem> {
        return this.crudService.postByUrl<TerminologyVocabularyPublicationHistoryItem>(
            this.apis.core.manual.postPlatformConfigurationTerminologyVocabularyPublicationRetire(publicationId),
            request,
            this.mutationOptions("postOptions", intent),
        ).pipe(map((response) => unwrapResponse(response)));
    }

    restoreVocabularyRevision(
        revisionId: number,
        request: TerminologyVocabularyRevisionRestoreRequest,
        intent = createTerminologyVocabularyMutationIntent(),
    ): Observable<TerminologyVocabularyPublicationDetail> {
        return this.crudService.postByUrl<TerminologyVocabularyPublicationDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyVocabularyRevisionRestore(revisionId),
            request,
            this.mutationOptions("postOptions", intent),
        ).pipe(map((response) => unwrapResponse(response)));
    }

    getEntry(entryId: number): Observable<TerminologyEntryDetail> {
        return this.crudService.getbyUrl<TerminologyEntryDetail>(
            this.apis.core.manual.getPlatformConfigurationTerminologyEntry(entryId),
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    createEntry(request: TerminologyEntryCreateRequest): Observable<TerminologyEntryDetail> {
        return this.crudService.postByUrl<TerminologyEntryDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyEntries(),
            request,
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    updateEntry(entryId: number, request: TerminologyEntryUpdateRequest): Observable<TerminologyEntryDetail> {
        return this.crudService.putByUrl<TerminologyEntryDetail>(
            this.apis.core.manual.putPlatformConfigurationTerminologyEntry(entryId),
            request,
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    publishEntry(entryId: number): Observable<TerminologyEntryDetail> {
        return this.crudService.postByUrl<TerminologyEntryDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyEntryPublish(entryId),
            null,
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    retireEntry(entryId: number): Observable<TerminologyEntryDetail> {
        return this.crudService.postByUrl<TerminologyEntryDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyEntryRetire(entryId),
            null,
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    rollbackEntry(entryId: number): Observable<TerminologyEntryDetail> {
        return this.crudService.postByUrl<TerminologyEntryDetail>(
            this.apis.core.manual.postPlatformConfigurationTerminologyEntryRollback(entryId),
            null,
            terminologyAdminApiOptions,
        ).pipe(map((response) => unwrapResponse(response)));
    }

    private mutationOptions(
        method: "postOptions" | "putOptions",
        intent: TerminologyVocabularyMutationIntent,
    ): Partial<CrudApiOptions<"body", "json">> {
        return {
            ...terminologyAdminApiOptions,
            [method]: {
                ...(terminologyAdminApiOptions[method] ?? {}),
                headers: requestHeaders.set("Idempotency-Key", intent.idempotencyKey),
            },
        };
    }
}

export { mapTerminologyAdminError };

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
    if (response && typeof response === "object" && "data" in response) {
        return (response as GenResp<T>).data;
    }

    return response as T;
}
